// «Tu agente» en la ventana «Santi» (propuesta §5.3, P9 sin el borrador — eso es la entrega 2): un cajón de 380 px que
// se abre cuando lo llamas desde la cabecera. En escritorio empuja la conversación (la caja y «Enviar» siguen a la
// vista); en el iPad la tapa, de la cabecera a la caja. Es UN agente para todo lo de Santi (el de General, construccion.md
// «Decisiones técnicas»): su chat de siempre (ConversacionOficina + Compositor) sobre el fondo cálido de lo tuyo. Entrega
// 2 (P9): sin tarjetas; lo que redacta para Santi lo deja como borrador en la caja del contrato (o del chat), y aquí
// contesta en una línea. Si aún no tiene agente: «Un agente se pone con esto» (se contrata al pulsar).
// 02-11 (Pau: los socios ven y hacen todo en la oficina): un socio lo ve con el título «El agente de Pau» y le escribe
// como a cualquiera (le llega firmado).
// 04-11 (ADR D-037): cabecera de 64 como la de VentanaChat (y a la altura de la de la conversación), título 15 y línea 13.
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useAppStore } from '../../../stores/app.store';
import { Compositor } from '../chat/Compositor';
import { ConversacionOficina } from '../chat/ConversacionOficina';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { mensajeError } from '../errores';
import { useMarcarLeido } from '../leidos';
import { useOficinaStore } from '../store';
import { Aviso, Boton, BotonIcono, IconoEncargo } from '../ui';
import { pelotasApi } from './api';
import { AvatarAgente } from './piezas';
import { PedidoEnElChat } from './PedidoEnElChat';
import { pedirHilo, useHablaComoPau, usePelotasStore } from './store';

export function TuAgente({ alCerrar, className }: { alCerrar: () => void; className?: string }) {
  const general = usePelotasStore((s) => s.general);
  const agentId = general?.agentId ?? null;
  const contratado = useOficinaStore((s) => (agentId ? s.contratados[agentId] : undefined));
  const comoPau = useHablaComoPau();
  // El agente de todo lo de Santi viaja con el chat con Santi: si aún no se ha leído, se lee al abrir el cajón.
  useEffect(() => {
    if (!usePelotasStore.getState().general) pedirHilo('general');
  }, []);
  return (
    <aside className={cn('flex h-full min-h-0 min-w-0 flex-col border-l border-oficina-acento/20 bg-oficina-tuyo', className)} aria-label={t(comoPau ? 'oficinaV2.pelotas.agente.titulo' : 'oficinaV2.pelotas.agente.dePau')} data-testid="tu-agente">
      <header className="flex h-16 shrink-0 items-center gap-3 border-b border-oficina-linea bg-oficina-tuyo-oscuro pl-4 pr-2">
        {/* El icono de su encargo, como en toda la oficina (Pau, 30-09); el robot mientras no hay agente. */}
        {contratado ? <IconoEncargo icono={contratado.icono} semilla={contratado.semillaAvatar} tamano={40} /> : <AvatarAgente tamano={40} />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-o-cuerpo font-semibold text-oficina-texto" data-testid="tu-agente-titulo">
            {t(comoPau ? 'oficinaV2.pelotas.agente.titulo' : 'oficinaV2.pelotas.agente.dePau')}
          </p>
          <p className="truncate text-o-sub text-oficina-texto2">{t('oficinaV2.pelotas.agente.sub')}</p>
        </div>
        <BotonIcono icono={X} etiqueta={t('oficinaV2.pelotas.agente.cerrar')} onClick={alCerrar} data-testid="tu-agente-cerrar" />
      </header>
      {!general ? (
        <p className="m-auto text-o-texto text-oficina-texto3">{t('oficinaV2.pelotas.agente.cargando')}</p>
      ) : !agentId ? (
        <SinAgente />
      ) : !contratado ? (
        <p className="m-auto text-o-texto text-oficina-texto3">{t('oficinaV2.pelotas.agente.cargando')}</p>
      ) : (
        <CuerpoAgente c={contratado} />
      )}
    </aside>
  );
}

/** El chat del agente, como en su ventana: carga su conversación y su expediente al abrirse; lo leído se marca. */
function CuerpoAgente({ c }: { c: ContratadoDTO }) {
  const agentId = c.agentId;
  const agent = useAppStore((s) => s.agents[agentId]);
  const messages = useAppStore((s) => s.messagesByAgent[agentId] ?? null);
  const loadMessages = useAppStore((s) => s.loadMessages);
  const abrirChat = useOficinaStore((s) => s.abrirChat);
  const expediente = useOficinaStore((s) => s.expedientes[agentId]);
  const [error, setError] = useState<string | null>(null);
  useMarcarLeido(c);
  useEffect(() => {
    abrirChat(agentId);
    return () => abrirChat(null);
  }, [abrirChat, agentId]);
  useEffect(() => {
    let vivo = true;
    setError(null);
    loadMessages(agentId).catch((e: unknown) => vivo && setError(mensajeError(e)));
    return () => {
      vivo = false;
    };
  }, [agentId, loadMessages]);
  return (
    <>
      <div className="relative flex min-h-0 flex-1 flex-col [&_[data-cola=agente]]:text-oficina-tuyo-globo [&_[data-globo=agente]]:bg-oficina-tuyo-globo">
        {error && messages === null ? (
          <p className="m-auto px-6 text-center text-o-texto text-oficina-error">{error}</p>
        ) : !agent || messages === null ? (
          <p className="m-auto text-o-texto text-oficina-texto3">{t('oficinaV2.pelotas.agente.cargando')}</p>
        ) : (
          <ConversacionOficina contratado={c} mensajes={messages} traducciones={expediente?.ficha?.traducciones} resumenes={expediente?.ficha?.resumenes} pie={<PedidoEnElChat agentId={agentId} />} />
        )}
      </div>
      <div className="[&_[data-compositor]]:bg-oficina-tuyo-oscuro">
        <Compositor key={agentId} agentId={agentId} placeholder={t('oficinaV2.pelotas.agente.escribe')} />
      </div>
    </>
  );
}

/** Sin agente todavía: se contrata el de todo lo de Santi (el encargo de General). */
function SinAgente() {
  const [poniendo, setPoniendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const poner = async () => {
    setPoniendo(true);
    setError(null);
    try {
      await pelotasApi.agente('general');
      pedirHilo('general');
      void useOficinaStore.getState().hidratar();
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setPoniendo(false);
    }
  };
  return (
    <div className="m-auto flex max-w-[300px] flex-col items-center gap-3 px-6 text-center" data-testid="sin-agente">
      <AvatarAgente tamano={40} />
      <h3 className="text-o-titulo3 font-semibold text-oficina-texto">{t('oficinaV2.pelotas.agente.sinAgente')}</h3>
      <p className="text-o-cuerpo leading-relaxed text-oficina-texto2">{t('oficinaV2.pelotas.agente.sinAgenteGeneral')}</p>
      <Boton variante="secundario" className="mt-1" cargando={poniendo} onClick={() => void poner()}>
        {t(poniendo ? 'oficinaV2.pelotas.agente.poniendo' : 'oficinaV2.pelotas.agente.poner')}
      </Boton>
      {error ? <Aviso tono="error">{error}</Aviso> : null}
    </div>
  );
}
